/**
 * DataManagerPowerUp.ts
 *
 * Runtime manager for powerup effect data.
 * Loads:
 * - powerups-overrides.json (effect definitions)
 *
 * Handles data-driven powerup effects when entities are abducted.
 * Follows the same pattern as DataManagerWeapon.
 */

import Phaser from 'phaser';
import { WeightClass } from '../constants/enums';

// ============================================================================
// PowerUp Types
// ============================================================================

export type EffectOperation = 'add' | 'subtract';

export interface PowerUpEffect {
  /** Target path using dot notation: "player.health", "weapon.bomb.plasmabomb" */
  target: string;
  /** Operation to apply */
  operation: EffectOperation;
  /** Value to apply */
  value: number;
}

export interface PowerUpDefinition {
  id: string;
  effects: PowerUpEffect[];
  bioMatter: number;
  weight: WeightClass;
  /** Notification template with {value} placeholder */
  notification: string;
  /** For ammo powerups: the weapon ID this ammo is for */
  weaponId?: string;
  /** For ammo powerups: display name of the weapon */
  weaponName?: string;
}

/** Config returned by getAmmoConfig for creating AmmoPowerUp instances */
export interface AmmoConfig {
  texture: string;
  weaponId: string;
  ammoAmount: number;
  weaponName: string;
}

// ============================================================================
// JSON Types
// ============================================================================

interface PowerUpOverrideEntry {
  effects: Array<{
    target: string;
    operation: 'add' | 'subtract';
    value: number;
  }>;
  bioMatter: number;
  weight: 'light' | 'medium' | 'heavy';
  notification: string;
  /** For ammo powerups: the weapon ID */
  weaponId?: string;
  /** For ammo powerups: display name */
  weaponName?: string;
}

interface PowerUpsOverridesFile {
  _meta?: {
    description: string;
    note: string;
  };
  powerups: Record<string, PowerUpOverrideEntry>;
}

// ============================================================================
// DataManagerPowerUp
// ============================================================================

/**
 * Static manager for powerup effect data.
 * Loads definitions from powerups-overrides.json at runtime.
 */
export class DataManagerPowerUp {
  private static powerups: Map<string, PowerUpDefinition> = new Map();
  /** Maps weaponId -> texture (powerup id) for ammo powerups */
  private static weaponIdToTexture: Map<string, string> = new Map();
  private static loaded: boolean = false;

  static readonly OVERRIDES_JSON_KEY = 'powerups-overrides';

  /**
   * Preload JSON files (call in scene preload)
   */
  static preload(scene: Phaser.Scene): void {
    if (!scene.cache.json.exists(this.OVERRIDES_JSON_KEY)) {
      scene.load.json(this.OVERRIDES_JSON_KEY, '/assets/data/powerups-overrides.json');
    }
  }

  /**
   * Load data from cache (call in scene create)
   */
  static load(scene: Phaser.Scene): void {
    // Skip if already loaded AND data exists
    if (this.loaded && this.powerups.size > 0) {
      return;
    }

    // Reset if data was lost but flag was still true
    if (this.loaded && this.powerups.size === 0) {
      console.warn('[DataManagerPowerUp] Data lost, reloading...');
      this.loaded = false;
    }

    // Load overrides
    const overridesJson = scene.cache.json.get(this.OVERRIDES_JSON_KEY) as PowerUpsOverridesFile | undefined;
    if (!overridesJson) {
      console.warn('[DataManagerPowerUp] powerups-overrides.json not loaded');
      return;
    }

    // Build powerup definitions
    if (overridesJson.powerups) {
      for (const [id, entry] of Object.entries(overridesJson.powerups)) {
        const definition = this.buildPowerUpDefinition(id, entry);
        this.powerups.set(id, definition);
      }
    }

    this.loaded = true;
    // console.log(`[DataManagerPowerUp] Loaded ${this.powerups.size} powerup definitions`);
  }

  /**
   * Build a powerup definition from JSON entry
   */
  private static buildPowerUpDefinition(
    id: string,
    entry: PowerUpOverrideEntry
  ): PowerUpDefinition {
    // Map weight string to WeightClass enum
    const weightMap: Record<string, WeightClass> = {
      light: WeightClass.Light,
      medium: WeightClass.Medium,
      heavy: WeightClass.Heavy,
    };

    const definition: PowerUpDefinition = {
      id,
      effects: entry.effects.map(e => ({
        target: e.target,
        operation: e.operation,
        value: e.value,
      })),
      bioMatter: entry.bioMatter ?? 0,
      weight: weightMap[entry.weight] ?? WeightClass.Light,
      notification: entry.notification ?? '',
    };

    // Add ammo-specific fields if present
    if (entry.weaponId) {
      definition.weaponId = entry.weaponId;
      definition.weaponName = entry.weaponName ?? entry.weaponId;
      // Build reverse lookup: weaponId -> texture (powerup id)
      this.weaponIdToTexture.set(entry.weaponId, id);
    }

    return definition;
  }

  // ============================================================================
  // Getters
  // ============================================================================

  /**
   * Get powerup definition by ID
   */
  static get(id: string): PowerUpDefinition | undefined {
    return this.powerups.get(id);
  }

  /**
   * Check if powerup definition exists
   */
  static has(id: string): boolean {
    return this.powerups.has(id);
  }

  /**
   * Get notification text with values substituted
   * Replaces {value} with the actual effect value(s)
   */
  static getNotificationText(id: string, effects?: PowerUpEffect[]): string {
    const def = this.powerups.get(id);
    if (!def) return '';

    let text = def.notification;
    const effectList = effects ?? def.effects;

    // Replace {value} with the first effect's value (most common case)
    if (effectList.length > 0) {
      text = text.replace('{value}', String(effectList[0].value));
    }

    return text;
  }

  /**
   * Get all powerup IDs
   */
  static getIds(): string[] {
    return Array.from(this.powerups.keys());
  }

  /**
   * Check if data has been loaded
   */
  static isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Reset manager (for testing or scene restarts)
   */
  static reset(): void {
    this.powerups.clear();
    this.weaponIdToTexture.clear();
    this.loaded = false;
  }

  // ============================================================================
  // Ammo PowerUp Helpers
  // ============================================================================

  /**
   * Check if a weaponId has an associated ammo powerup
   */
  static isAmmoType(weaponId: string): boolean {
    return this.weaponIdToTexture.has(weaponId);
  }

  /**
   * Get ammo config by weaponId (for creating AmmoPowerUp instances)
   * Returns null if weaponId is not found
   */
  static getAmmoConfig(weaponId: string): AmmoConfig | null {
    const texture = this.weaponIdToTexture.get(weaponId);
    if (!texture) {
      console.error(`[DataManagerPowerUp] No ammo powerup found for weaponId: ${weaponId}`);
      return null;
    }

    const def = this.powerups.get(texture);
    if (!def || !def.weaponId) {
      console.error(`[DataManagerPowerUp] PowerUp definition missing for texture: ${texture}`);
      return null;
    }

    // Get ammo amount from the first effect's value
    const ammoAmount = def.effects.length > 0 ? def.effects[0].value : 0;

    return {
      texture,
      weaponId: def.weaponId,
      ammoAmount,
      weaponName: def.weaponName ?? def.weaponId,
    };
  }

  /**
   * Get all known ammo weaponIds
   */
  static getAmmoWeaponIds(): string[] {
    return Array.from(this.weaponIdToTexture.keys());
  }
}
